import {
  Button,
  FlexBox,
  Icon,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useAtomValue } from 'jotai';
import CategoryTreeInput from 'lib-knowledge-management-common/components/categoryTreeInput/index';
import { CategoryNodeQuery } from 'lib-knowledge-management-common/graphql';
import { ParentRef } from 'lib-knowledge-management-common/models/types';
import { extractTitleFromTranslations } from 'lib-knowledge-management-common/utils/translations';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { collectionIdAtom } from 'models/collectionStore';
import { useCallback, useEffect, useState } from 'react';

import useCategoryFetch from 'hooks/useCategoryFetch';
import useCategoryMove from 'hooks/useCategoryMove';

import s from './styles.module.scss';

type CategoryMoveProps = {
  category: NonNullable<CategoryNodeQuery['category']>;
  newParent: ParentRef;
};

type CategoryMoveModalProps = {
  categoryId: string;
};

export function CategoryMoveModal({
  categoryId,
}: CategoryMoveModalProps): JSX.Element {
  const collectionId = useAtomValue(collectionIdAtom);
  const { successNotify, errorNotify } = useNotification();
  const { hideModal } = useModal();
  const [move, { loading, called }] = useCategoryMove();
  const { data } = useCategoryFetch({
    categoryId,
  });
  const category = data?.category;

  const [newParentCategory, setNewParentCategory] = useState<
    ParentRef | undefined
  >(undefined);

  useEffect(() => {
    setNewParentCategory(category?.parent || undefined);
  }, [category]);

  const moveCategory = useCallback(
    /** Handles category moving as well as notification for success/failure */
    function moveCategoryAndNotify({
      category: movingCategory,
      newParent: newParentRef,
    }: CategoryMoveProps) {
      const movingCategoryTitle = extractTitleFromTranslations(
        movingCategory.titles,
      );
      return move({
        categoryId: movingCategory.id,
        newParentRef,
      })
        .then(() => {
          hideModal();
          successNotify({
            title: 'Category Moved',
            text: `"${movingCategoryTitle}" has been moved`,
          });
        })
        .catch(() => {
          errorNotify({
            text: `"${movingCategoryTitle}" could not be moved`,
          });
        });
    },
    [hideModal, move, successNotify, errorNotify],
  );

  if (!category || !newParentCategory) return <></>;

  const isDisabled =
    loading ||
    called ||
    // Can't move if parent doesn't change
    newParentCategory.id === category.parent?.id;

  const categoryTitle = extractTitleFromTranslations(category?.titles);

  return (
    <ModalWrapper>
      <ModalHead title="Move Category" onClose={hideModal}></ModalHead>
      <ModalBody>
        <div className={s.categoryPreview}>
          <FlexBox height="100%" alignItems="center">
            <Icon name="file" size={20} className={s.icon} />
            <FlexBox flexDirection="column">
              <div>
                <Text.Heading variant="heading-2">{categoryTitle}</Text.Heading>
                <div className={s.meta}>
                  {!!category.categories?.length && (
                    <span>
                      {category.categories.length}&nbsp;
                      {category.categories.length === 1
                        ? 'category'
                        : 'categories'}
                    </span>
                  )}

                  {!!category.categories?.length &&
                    !!category.articles?.length && (
                      <span>&nbsp;&bull;&nbsp;</span>
                    )}

                  {!!category.articles?.length && (
                    <span>
                      {category.articles.length}&nbsp;
                      {category.articles.length > 1 ? 'articles' : 'article'}
                    </span>
                  )}
                </div>
              </div>
            </FlexBox>
          </FlexBox>
        </div>

        <CategoryTreeInput
          withCollectionOption
          value={newParentCategory}
          onChange={setNewParentCategory}
          currentCategoryId={category.id}
          collectionId={collectionId}
        />

        <FlexBox justifyContent="flex-end" gap="100">
          <Button
            disabled={loading || called}
            variant="tertiary"
            onClick={hideModal}
          >
            Cancel
          </Button>
          <Button
            disabled={isDisabled}
            onClick={() =>
              moveCategory({
                category,
                newParent: newParentCategory,
              })
            }
          >
            Save
          </Button>
        </FlexBox>
      </ModalBody>
    </ModalWrapper>
  );
}
